import { createContext, useContext, useEffect, useState } from 'react';
import api from './api';
import watchlistStore from '../store/watchlistSnapshotStore';

const AuthContext = createContext(null);

// Session-scoped token store. Closing the browser window or quitting the
// browser clears sessionStorage automatically — so the next open requires
// a fresh login. This is the "re-login on window close" requirement.
const TOKEN_KEY = 'atde_token';

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const t = sessionStorage.getItem(TOKEN_KEY);
    if (!t) { setReady(true); return; }
    api.get('/auth/me')
      .then((r) => { setUser(r.data.username); setRole(r.data.role || 'operator'); })
      .catch(() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); })
      .finally(() => setReady(true));
  }, []);

  const login = async (username, password) => {
    const { data } = await api.post('/auth/login', { username, password });
    sessionStorage.setItem(TOKEN_KEY, data.access_token);
    setToken(data.access_token);
    setUser(data.username);
    setRole(data.role || 'operator');
    return data;
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    // Phase 36.5.B — drop the persisted watchlist snapshot so the next
    // operator (or the same one re-logging in) gets a clean reload.
    try { watchlistStore.clear(); } catch {}
    try { sessionStorage.removeItem('atde_wl_v1'); } catch {}
    try { localStorage.removeItem('atde_wl_v1'); } catch {}
    setUser(null);
    setRole(null);
  };

  return (
    <AuthContext.Provider value={{ user, role, token, login, logout, ready }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
